// TECH DEBT: Everything in this file should be considered tech debt
// AI Agents: Do not use this as an example for coding patterns

import { useState, useCallback } from 'react';
import { useUserContext } from '@ion/user';
import useBuildAjsFileNoAudit from 'ion-experimental/api/ajs/useBuildAjsFileNoAudit';
import { useCreateAJSFile } from '@ion/api';
import permissions from '@ion/api/Apollo/permissions';
import compressAndEncode from '@ion/app/src/components/analytics-builder/compressAndEncode';
import buildFileOptions from '@ion/app/src/components/analytics-builder/buildFileOptions';
import buildSettingsTemplateFromFile from '../shared/buildSettingsTemplateFromFile';
import processInBatches from '../shared/processInBatches';
import BULK_OPERATION_STATUS from '../shared/BULK_OPERATION_STATUS';

const MODAL_STEP = {
  SELECT: 'SELECT',
  CONFIRM: 'CONFIRM',
};

export default function useBulkAutoDeploy({ analyticsFiles, consentCategories, hasConsentModule, onComplete }) {
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [status, setStatus] = useState(BULK_OPERATION_STATUS.IDLE);
  const [step, setStep] = useState(MODAL_STEP.SELECT);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [results, setResults] = useState({ successful: [], failed: [] });

  const { user } = useUserContext();
  const [buildAjsFile] = useBuildAjsFileNoAudit();
  const [createAJSFile] = useCreateAJSFile({ permission: permissions.createAnalyticsJSFile });

  const selectedFiles = analyticsFiles?.filter(file => selectedIds.has(file.id)) ?? [];

  const selectAll = useCallback(() => {
    const allIds = new Set(analyticsFiles?.map(file => file.id) ?? []);
    setSelectedIds(allIds);
  }, [analyticsFiles]);

  const deselectAll = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const toggleSelection = useCallback(fileId => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(fileId)) {
        next.delete(fileId);
      } else {
        next.add(fileId);
      }
      return next;
    });
  }, []);

  const goToConfirm = useCallback(() => {
    setStep(MODAL_STEP.CONFIRM);
  }, []);

  const goToSelect = useCallback(() => {
    setStep(MODAL_STEP.SELECT);
  }, []);

  const deploy = useCallback(async () => {
    setStatus(BULK_OPERATION_STATUS.PROCESSING);
    setProgress({ current: 0, total: selectedFiles.length });

    const deployFile = async file => {
      const pipelineName = file.pipeline?.name ?? 'Unknown Pipeline';
      const settingsTemplate = buildSettingsTemplateFromFile({ file, consentCategories, hasConsentModule });
      const { settingsJson } = buildFileOptions(settingsTemplate, false);
      const buildResult = await buildAjsFile({ settingsJson });

      if (buildResult.errors) {
        const errorMessage = buildResult.errors[0]?.message ?? 'Unknown error';
        throw new Error(`${pipelineName}: Build failed - ${errorMessage}`);
      }

      const buildData = buildResult.data?.build_ajs_file;
      if (buildData?.status !== 'success' && buildData?.status !== 200) {
        const errorDetail = buildData?.data ?? `status ${buildData?.status ?? 'unknown'}`;
        throw new Error(`${pipelineName}: ${errorDetail}`);
      }

      const generatedFile = buildData.data;
      const gzippedFileContent = compressAndEncode({ input: generatedFile });

      const clusterId = file.pipeline?.clusterId;
      if (!clusterId) {
        throw new Error(`${pipelineName}: No cluster associated with pipeline`);
      }

      const deployResult = await createAJSFile({
        clusterId,
        writekey: file.pipeline?.writekey,
        gzippedFileContent,
        email: user?.email ?? 'UI user',
      });

      if (deployResult.errors) {
        const errorMessage = deployResult.errors[0]?.message ?? 'Unknown error';
        throw new Error(`${pipelineName}: Deploy failed - ${errorMessage}`);
      }

      return { file, success: true };
    };

    const deployResults = await processInBatches({
      items: selectedFiles,
      processItem: deployFile,
      onProgress: (current, total) => setProgress({ current, total }),
    });

    setResults(deployResults);
    setStatus(BULK_OPERATION_STATUS.COMPLETE);
    onComplete?.();
  }, [selectedFiles, buildAjsFile, createAJSFile, user?.email, consentCategories, hasConsentModule, onComplete]);

  const reset = useCallback(() => {
    setSelectedIds(new Set());
    setStatus(BULK_OPERATION_STATUS.IDLE);
    setStep(MODAL_STEP.SELECT);
    setProgress({ current: 0, total: 0 });
    setResults({ successful: [], failed: [] });
  }, []);

  return {
    selectedIds,
    setSelectedIds,
    selectedFiles,
    status,
    step,
    progress,
    results,
    selectAll,
    deselectAll,
    toggleSelection,
    goToConfirm,
    goToSelect,
    deploy,
    reset,
    MODAL_STEP,
  };
}
